/**
 * InvestmentItemsSection - Investment items display for renovation tab
 *
 * Features:
 * - UniversalTable with lazy loading
 * - Multi-select rows for bulk operations
 * - Statistics showing total cost and counts
 * - Create/Edit/Delete individual items via InvestmentItemModal
 * - Integration with ChangeWithReasonModal for budget/status changes
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { ListBullets, Plus, ArrowSquareOut, Trash, Warning, Rocket, CaretDown, CaretUp, LinkBreak, FunnelSimple, X } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { UniversalTable } from "shared/components/UniversalTable";
import {
  InvestmentItemWithCalculations,
  RenovationLabel,
  InvestmentItemsWithAssignmentsResponse,
} from "screens/investment/types/investment";
import { InvestmentItemModal } from "screens/investment/components/InvestmentItemModal";
import { ChangeWithReasonModal } from "screens/investment/components/ChangeWithReasonModal";
import { BudgetTransferModal } from "screens/investment/components/BudgetTransferModal";
import { ConvertToProjectModal } from "screens/investment/components/ConvertToProjectModal";
import { CompleteInvestmentLineModal } from "screens/investment/components/CompleteInvestmentLineModal";
import { ReleaseInvestmentItemDialog } from "screens/investment/components/ReleaseInvestmentItemDialog";
import { BucketAllocationDialog } from "screens/investment/components/BucketAllocationDialog";
import { BudgetSplitDesigner } from "screens/investment/components/BudgetSplitDesigner";
import { INVESTMENT_BUILDING_CONTEXT_TABLE_CONFIG } from "screens/investment/configs/investmentTableConfig";
import { createInvestmentItemCells } from "screens/investment/shared/investmentCellFactory";
import { buildInvestmentFooterCells } from "screens/investment/shared/investmentFooterCells";
import {
  useBudgetBuckets,
  dispatchBudgetBucketsChanged,
} from "screens/investment/hooks/useBudgetBuckets";
import { BudgetPipelineFilter } from "screens/investment/shared/BudgetPipelineFilter";
import { BucketAmountSource } from "screens/investment/types/budget-bucket";
import { useUsers } from "shared/contexts/users.context";

interface InvestmentItemsSectionProps {
  buildingId: string;
  onFilterChange?: (filters: InvestmentItemsFilters) => void;
}

const BUDGET_FILTER_LABELS: Record<
  | "totaal"
  | "ruwGeschat"
  | "gedetailleerd"
  | "opgenomen"
  | "werkelijkeUitgegeven",
  string
> = {
  totaal: "Strategisch budget totaal",
  ruwGeschat: "Ruw geschat budget over projecten",
  // Bucket kept around because the "Strategisch budget gedetailleerd" read-only
  // pill still surfaces this number — but no filter pill uses it directly.
  gedetailleerd: "Strategisch budget gedetailleerd",
  opgenomen: "Projectonderdelen detailbudget",
  werkelijkeUitgegeven: "Werkelijke uitgegeven",
};

/** Korte bedragweergave voor de onderregels van de KPI-tegels. */
const formatEuro = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export interface InvestmentItemsFilters {
  searchText: string;
  statusFilter: string[];
  labelFilter: number[];
}

// Renders children into the renovation tab's #budget-pipeline-portal node so
// the BudgetPipelineFilter pills appear above the strategische budgetten
// section while the controlling state stays here in InvestmentItemsSection.
// Falls back to inline rendering if the portal target hasn't mounted yet
// (e.g. when this section is mounted in isolation in tests).
const PortalToBudgetPipeline = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const node = document.getElementById("budget-pipeline-portal");
    setTarget(node);
  }, []);

  if (!target) return <>{children}</>;
  return createPortal(children, target);
};

export const InvestmentItemsSection = React.memo(
  ({ buildingId, onFilterChange }: InvestmentItemsSectionProps) => {
    const { session } = useSession();
    const { users } = useUsers();
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLDivElement>(null);

    // Local items state for optimistic updates
    const [localItems, setLocalItems] = useState<InvestmentItemWithCalculations[]>([]);

    // Building-wide totals from the backend. `totalInvoiced` is summed across
    // ALL project parts on the building (not just parts linked to investment
    // items), so legacy projects without investment-item links still count.
    // `unlinkedPartsCount` powers the data-quality KPI.
    // `totalRoughBudget` is the SUM(roughBudgetEstimate) over the building's
    // renovation projects — the top-down budget benchmark per project,
    // surfaced as the "Ruw geschat budget over projecten" KPI.
    // `totalApprovedQuotes` is de som van de goedgekeurde offertes op het gebouw
    // en voedt de tegel "Goedgekeurde offertes" onder Werkelijke kostprognose.
    const [buildingTotals, setBuildingTotals] = useState<{
      totalInvoiced: number;
      unlinkedPartsCount: number;
      totalRoughBudget: number;
      totalApprovedQuotes: number;
    }>({
      totalInvoiced: 0,
      unlinkedPartsCount: 0,
      totalRoughBudget: 0,
      totalApprovedQuotes: 0,
    });

    // Filter states (for FilterBar integration) - controlled by parent
    const [searchText] = useState("");
    const [statusFilter] = useState<string[]>([]);
    const [labelFilter] = useState<number[]>([]);

    // Selection state
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

    // Modal states
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<InvestmentItemWithCalculations | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletingItem, setDeletingItem] = useState<InvestmentItemWithCalculations | null>(null);
    const [convertModalOpen, setConvertModalOpen] = useState(false);
    const [budgetChangeModalOpen, setBudgetChangeModalOpen] = useState(false);
    const [budgetChangeItem, setBudgetChangeItem] = useState<InvestmentItemWithCalculations | null>(null);
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
    const [statusChangeItem, setStatusChangeItem] = useState<InvestmentItemWithCalculations | null>(null);
    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [transferSourceItem, setTransferSourceItem] = useState<InvestmentItemWithCalculations | null>(null);
    const [transferFreedAmount, setTransferFreedAmount] = useState(0);
    const [completeModalOpen, setCompleteModalOpen] = useState(false);
    const [releasingItem, setReleasingItem] =
      useState<InvestmentItemWithCalculations | null>(null);
    const [completeItem, setCompleteItem] =
      useState<InvestmentItemWithCalculations | null>(null);

    // Renovation labels state (for label picker)
    const [renovationLabels, setRenovationLabels] = useState<RenovationLabel[]>([]);

    // Loading state
    const [isLoading, setIsLoading] = useState(true);

    // Section expand/collapse state
    const [isExpanded, setIsExpanded] = useState(true);

    // Budget pipeline filter (segmented control above table)
    const [budgetFilter, setBudgetFilter] = useState<
      | "totaal"
      | "ruwGeschat"
      | "gedetailleerd"
      | "opgenomen"
      | "werkelijkeUitgegeven"
    >("totaal");

    // Click handler for the pipeline pills.
    // - In-page filter: totaal, ruwGeschat, gedetailleerd, opgenomen.
    // - Werkelijke uitgegeven (Gefactureerd): opens the financiële documenten
    //   page in a new tab pre-filtered to this building + type=Factuur.
    const handleBudgetPillClick = useCallback(
      (
        next:
          | "totaal"
          | "ruwGeschat"
          | "gedetailleerd"
          | "opgenomen"
          | "werkelijkeUitgegeven",
      ) => {
        if (next === "werkelijkeUitgegeven") {
          window.open(
            `/renovation/documents?buildingId=${encodeURIComponent(
              buildingId,
            )}&documentType=Factuur`,
            "_blank",
            "noopener,noreferrer",
          );
          return;
        }
        setBudgetFilter(next);
      },
      [buildingId],
    );

    // Gefactureerd KPI is sourced from `data.totals.totalInvoiced` returned
    // by the items endpoint — the BE aggregates invoice lines via a single
    // SQL query (DocumentLine ⨝ RenovationDocument ⨝ Building, type=INVOICE).
    // We used to do a second roundtrip + JS loop over the project tree here;
    // it duplicated the BE work and was the dominant load-time cost.

    // Fetch investment items
    const fetchItems = useCallback(async () => {
      if (!session || !buildingId) {
        console.log("[InvestmentItemsSection] Skipping fetch - no session or buildingId", { session: !!session, buildingId });
        return;
      }

      try {
        setIsLoading(true);
        console.log("[InvestmentItemsSection] Fetching investment items for building:", buildingId);
        const response = await fetchWithAuth(
          `${API_URL}/investment/building/${buildingId}/with-assignments`,
          { method: "GET" },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch investment items: ${response.status} ${response.statusText}`);
        }

        const data: InvestmentItemsWithAssignmentsResponse = await response.json();
        console.log("[InvestmentItemsSection] Received data:", data);
        console.log("[InvestmentItemsSection] Items count:", data.items?.length || 0);
        setLocalItems(data.items || []);
        setBuildingTotals({
          totalInvoiced: Number(data.totals?.totalInvoiced) || 0,
          unlinkedPartsCount: Number(data.totals?.unlinkedPartsCount) || 0,
          totalRoughBudget: Number(data.totals?.totalRoughBudget) || 0,
          totalApprovedQuotes: Number(data.totals?.totalApprovedQuotes) || 0,
        });
      } catch (error) {
        console.error("[InvestmentItemsSection] Error fetching investment items:", error);
        toast.error("Fout bij ophalen budget detailverdeling");
      } finally {
        setIsLoading(false);
      }
    }, [session, buildingId]);

    // Initial fetch — totalInvoiced rides along inside `data.totals`.
    useEffect(() => {
      fetchItems();
    }, [fetchItems]);

    // Notify parent of filter changes
    useEffect(() => {
      onFilterChange?.({
        searchText,
        statusFilter,
        labelFilter,
      });
    }, [searchText, statusFilter, labelFilter, onFilterChange]);

    // Fetch renovation labels for the label picker
    useEffect(() => {
      const fetchLabels = async () => {
        if (!session) return;
        try {
          const response = await fetchWithAuth(
            `${API_URL}/renovation/labels`,
            { method: "GET" },
            session
          );
          if (response.ok) {
            const data = await response.json();
            setRenovationLabels(data || []);
          }
        } catch (error) {
          console.error("Failed to fetch renovation labels:", error);
        }
      };
      fetchLabels();
    }, [session]);

    // Listen for budget change events from the table cell
    useEffect(() => {
      const handleBudgetChangeEvent = (event: CustomEvent) => {
        const { item } = event.detail;
        if (item) {
          setBudgetChangeItem(item);
          setBudgetChangeModalOpen(true);
        }
      };

      window.addEventListener(
        "investment-budget-change",
        handleBudgetChangeEvent as EventListener
      );

      return () => {
        window.removeEventListener(
          "investment-budget-change",
          handleBudgetChangeEvent as EventListener
        );
      };
    }, []);

    // Listen for status change events from the table cell
    useEffect(() => {
      const handleStatusChangeEvent = (event: CustomEvent) => {
        const { item } = event.detail;
        if (item) {
          setStatusChangeItem(item);
          setStatusChangeModalOpen(true);
        }
      };

      window.addEventListener(
        "investment-status-change",
        handleStatusChangeEvent as EventListener
      );

      return () => {
        window.removeEventListener(
          "investment-status-change",
          handleStatusChangeEvent as EventListener
        );
      };
    }, []);

    // Multi-pot bucket allocation dialog — opened from the bucket cell.
    const [bucketAllocItem, setBucketAllocItem] =
      useState<InvestmentItemWithCalculations | null>(null);
    useEffect(() => {
      const handler = (event: CustomEvent) => {
        const { item } = event.detail || {};
        if (item) setBucketAllocItem(item);
      };
      window.addEventListener(
        "investment-bucket-allocation",
        handler as EventListener,
      );
      return () => {
        window.removeEventListener(
          "investment-bucket-allocation",
          handler as EventListener,
        );
      };
    }, []);

    // Buckets for this building — used both for the section above, as
    // dynamic options in the items table's bucket picker, AND to compute
    // the "Strategisch budget totaal" KPI tile.
    const { buckets } = useBudgetBuckets(buildingId);

    // KPI tiles above the table. Each entry exposes:
    //   total: number           → currency shown on the pill
    //   rows: []                → which rows the table is filtered to when active
    //   overflowAmount?, overflowLabel? → optional sub-line under the tile
    //                                     showing project-side overflow
    //
    // Definitions:
    //   totaal               = sum of all real (non-overflow) bucket totalAmount
    //                          (group: Budget planning over gebouw)
    //   ruwGeschat           = sum of project.roughBudgetEstimate (locked when
    //                          present) across the building's projects
    //                          (group: Budget uitwerking in projecten)
    //                          ↳ overflow: max(0, opgenomen − ruwGeschat)
    //                            "overflow in ruwe inschatting"
    //   gedetailleerd        = sum over lines of strategic = bucket
    //                          ? min(est, bucket.totalAmount || est) : 0
    //                          ↳ overflow: max(0, opgenomen − gedetailleerd)
    //                            "overflow in projectonderdelen"
    //   opgenomen            = sum of allocatedBudget across all lines
    //                          (renamed from "Opgenomen in projecten")
    //   werkelijkeUitgegeven = building-wide invoiced total (from project
    //                          parts via SQL aggregate) — group on its own.
    const budgetBuckets = useMemo(() => {
      const strategicForLine = (i: InvestmentItemWithCalculations) => {
        if (!i.bucket?.id) return 0;
        const est = Number(i.estimatedCostExclVat) || 0;
        const cap = Number(i.bucket?.totalAmount) || est;
        return Math.min(est, cap || est);
      };

      const echteBuckets = buckets.filter((b) => !b.isOverflow);
      const totaalAmount = echteBuckets.reduce(
        (s, b) => s + (Number(b.totalAmount) || 0),
        0,
      );

      // Strategisch budget = acquisitiebudget + de andere strategische budgetten.
      // "Zit het acquisitiebudget hierin?" was een terugkerende vraag bij beide
      // tegels, dus staat het antwoord er nu onder in plaats van in iemands hoofd.
      const acquisitieBuckets = echteBuckets.filter(
        (b) => b.amountSource === BucketAmountSource.ACQUISITION_RENOVATION_BUDGET,
      );
      const acquisitieTotaal = acquisitieBuckets.reduce(
        (s, b) => s + (Number(b.totalAmount) || 0),
        0,
      );
      const acquisitieBucketIds = new Set(acquisitieBuckets.map((b) => b.id));

      const ruwGeschatTotal = buildingTotals.totalRoughBudget;
      // For row filtering: ruwGeschat is a project-level value, so the
      // "rows" view falls back to all items (no per-line slice exists).
      const ruwGeschatRows = localItems;

      const gedetailleerdRows = localItems.filter(
        (i) => strategicForLine(i) > 0,
      );
      const gedetailleerdTotal = localItems.reduce(
        (s, i) => s + strategicForLine(i),
        0,
      );
      // Het deel van "gedetailleerd" dat uit een acquisitiebudget komt: enkel de
      // lijnen die op zo'n pot getrokken zijn. Het acquisitiebudget zit dus wél
      // in deze tegel, maar alleen voor wat er effectief aan lijnen hangt.
      const gedetailleerdAcquisitie = localItems.reduce(
        (s, i) =>
          i.bucket?.id && acquisitieBucketIds.has(i.bucket.id)
            ? s + strategicForLine(i)
            : s,
        0,
      );

      const opgenomenRows = localItems.filter(
        (i) => Number(i.allocatedBudget) > 0,
      );
      const opgenomenTotal = localItems.reduce(
        (s, i) => s + (Number(i.allocatedBudget) || 0),
        0,
      );

      // Two distinct overflow flavours, each pinned under its baseline tile:
      //  • overflow in ruwe inschatting     = opgenomen − ruwGeschat
      //  • overflow in projectonderdelen    = opgenomen − gedetailleerd
      const overflowVsRuwGeschat = Math.max(
        0,
        opgenomenTotal - ruwGeschatTotal,
      );
      const overflowVsGedetailleerd = Math.max(
        0,
        opgenomenTotal - gedetailleerdTotal,
      );

      const gefactureerdRows = localItems.filter(
        (i) => Number(i.invoicedAmount || 0) > 0,
      );

      return {
        totaal: {
          rows: localItems,
          total: totaalAmount,
          subline:
            acquisitieTotaal > 0
              ? {
                  text: `waarvan ${formatEuro(acquisitieTotaal)} acquisitiebudget + ${formatEuro(
                    totaalAmount - acquisitieTotaal,
                  )} andere`,
                  tone: "neutral" as const,
                }
              : undefined,
        },
        ruwGeschat: {
          rows: ruwGeschatRows,
          total: ruwGeschatTotal,
          overflowAmount: overflowVsRuwGeschat,
          overflowLabel: "overflow in ruwe inschatting",
        },
        gedetailleerd: {
          rows: gedetailleerdRows,
          total: gedetailleerdTotal,
          overflowAmount: overflowVsGedetailleerd,
          overflowLabel: "overflow in projectonderdelen",
          subline:
            acquisitieTotaal > 0
              ? {
                  text:
                    gedetailleerdAcquisitie > 0
                      ? `waarvan ${formatEuro(gedetailleerdAcquisitie)} acquisitiebudget`
                      : "acquisitiebudget nog niet op een investeringslijn",
                  tone: "neutral" as const,
                }
              : undefined,
        },
        opgenomen: {
          rows: opgenomenRows,
          total: opgenomenTotal,
        },
        // Pill total is the building-wide invoice sum so legacy projects whose
        // parts aren't linked to any investment item still get reflected here.
        werkelijkeUitgegeven: {
          rows: gefactureerdRows,
          total: buildingTotals.totalInvoiced,
        },
      };
    }, [
      localItems,
      buckets,
      buildingTotals.totalInvoiced,
      buildingTotals.totalRoughBudget,
    ]);

    // Use filtered items for display
    const items = budgetBuckets[budgetFilter].rows;

    // Calculate selected items statistics
    const selectedItemsStats = useMemo(() => {
      const selectedItems = items.filter((item) => selectedRows.has(item.id));
      const totalCost = selectedItems.reduce(
        (sum, item) => sum + (Number(item.estimatedCostExclVat) || 0),
        0
      );

      return {
        count: selectedItems.length,
        totalCost,
        items: selectedItems,
      };
    }, [items, selectedRows]);

    // Handler functions
    const handleModalSuccess = useCallback(() => {
      fetchItems();
    }, [fetchItems]);

    const handleTransferRequest = useCallback((item: InvestmentItemWithCalculations, freedAmount: number) => {
      setTransferSourceItem(item);
      setTransferFreedAmount(freedAmount);
      setTransferModalOpen(true);
    }, []);

    // Clicking a row opens the unified edit modal directly. The legacy
    // read-only drawer has been retired in favour of a single modal that
    // exposes every editable field plus the audit trail.
    const handleRowClick = useCallback((item: InvestmentItemWithCalculations) => {
      setEditingItem(item);
      setEditModalOpen(true);
    }, []);

    const handleEditClick = useCallback((item: InvestmentItemWithCalculations) => {
      setEditingItem(item);
      setEditModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((item: InvestmentItemWithCalculations) => {
      setDeletingItem(item);
      setDeleteModalOpen(true);
    }, []);

    const handleCompleteClick = useCallback(
      (item: InvestmentItemWithCalculations) => {
        setCompleteItem(item);
        setCompleteModalOpen(true);
      },
      [],
    );

    const handleReleaseClick = useCallback(
      (item: InvestmentItemWithCalculations) => {
        setReleasingItem(item);
      },
      [],
    );

    const handleReleaseConfirm = useCallback(
      async (reason: string | undefined) => {
        if (!releasingItem) return;
        const total = Number(releasingItem.estimatedCostExclVat) || 0;
        const used = Number(releasingItem.allocatedBudget) || 0;
        const remainder = Math.max(0, total - used);
        const fmt = new Intl.NumberFormat("nl-BE", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        });
        try {
          const response = await fetchWithAuth(
            `${API_URL}/investment/items/${releasingItem.id}/release`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(reason ? { reason } : {}),
            },
            session,
          );
          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || "Failed to release item");
          }
          toast.success(
            `Lijn "${releasingItem.title}" vrijgegeven — ${fmt.format(remainder)} terug naar de pot`,
          );
          setReleasingItem(null);
          fetchItems();
          dispatchBudgetBucketsChanged(buildingId);
        } catch (error) {
          console.error("Error releasing item:", error);
          toast.error(
            error instanceof Error ? error.message : "Fout bij vrijgeven",
          );
        }
      },
      [releasingItem, session, fetchItems, buildingId],
    );

    const handleDeleteConfirm = useCallback(async () => {
      if (!deletingItem) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/items/${deletingItem.id}`,
          { method: "DELETE" },
          session
        );

        if (response.ok) {
          toast.success(`Item "${deletingItem.title}" verwijderd`);
          setDeleteModalOpen(false);
          setDeletingItem(null);
          fetchItems();
          // Deleting a non-terminal line releases its budget back to the
          // bucket; refresh the cards so PMs see the new "used" totals.
          dispatchBudgetBucketsChanged(buildingId);
        } else {
          throw new Error("Failed to delete item");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("Fout bij verwijderen van item");
      }
    }, [deletingItem, session, fetchItems, buildingId]);

    const handleNavigateToInvestmentList = useCallback(() => {
      navigate(`/renovation/investments?buildingId=${buildingId}`);
    }, [navigate, buildingId]);

    // "Nieuwe budgetlijn" now opens the budget split designer with no bucket
    // pre-selected — the designer renders the pot picker (incl. Overflow) so
    // the user picks where the lines should land.
    const [splitDesignerOpen, setSplitDesignerOpen] = useState(false);
    const handleAddInvestmentItem = useCallback(() => {
      setSplitDesignerOpen(true);
    }, []);

    const handleConvertToProject = useCallback(() => {
      setConvertModalOpen(true);
    }, []);

    // Handle optimistic update
    const handleUpdate = useCallback(
      (itemId: number, updates: any) => {
        setLocalItems((prevItems) =>
          prevItems.map((item) => {
            if (item.id !== itemId) return item;

            const updatedItem = { ...item };

            if (updates.labelId !== undefined) {
              const label = renovationLabels.find((l) => l.id === updates.labelId);
              updatedItem.label = label;
            }
            if (updates.budgetLabel !== undefined) {
              updatedItem.budgetLabel = updates.budgetLabel;
            }
            if (updates.responsibleUserId !== undefined) {
              updatedItem.responsibleUserId = updates.responsibleUserId;
            }
            if (updates.title !== undefined) {
              updatedItem.title = updates.title;
            }
            if (updates.estimatedCostExclVat !== undefined) {
              updatedItem.estimatedCostExclVat = updates.estimatedCostExclVat;
            }
            if (updates.bucketId !== undefined) {
              const next = updates.bucketId
                ? buckets.find((b) => b.id === updates.bucketId)
                : null;
              updatedItem.bucket = next
                ? {
                    id: next.id,
                    name: next.name,
                    isApproved: next.isApproved,
                  }
                : null;
            }

            return updatedItem;
          })
        );

        // Any bucket movement or budget delta changes the bucket "used"
        // amount on the BE side — refresh the bucket cards above the table
        // so the numbers stay in sync with the items list.
        const affectsBucket =
          updates.bucketId !== undefined ||
          updates.estimatedCostExclVat !== undefined;
        if (affectsBucket) {
          dispatchBudgetBucketsChanged(buildingId);
        }
      },
      [renovationLabels, buckets, buildingId]
    );

    // Render row helper
    const renderRow = useCallback(
      (item: InvestmentItemWithCalculations, context: any) => {
        return createInvestmentItemCells({
          item: item as any, // Cast to match the shared factory's type
          context,
          handlers: {
            onRowClick: handleRowClick as any,
            onEditClick: handleEditClick as any,
            onDeleteClick: handleDeleteClick as any,
            onCompleteClick: handleCompleteClick as any,
            onReleaseClick: handleReleaseClick as any,
          },
          users,
        });
      },
      [
        handleRowClick,
        handleEditClick,
        handleDeleteClick,
        handleCompleteClick,
        handleReleaseClick,
        users,
      ]
    );

    // Totals row renderer — sums Nodig budget and In project across visible rows
    const renderFooterCells = useCallback(
      (visibleRows: InvestmentItemWithCalculations[]) =>
        buildInvestmentFooterCells(visibleRows),
      []
    );

    // Create dynamic picker config with renovation labels and budget buckets.
    // Each picker mapping has its `customOptions` filled in from data fetched
    // on this page so the modal always shows fresh, building-scoped values.
    const dynamicPickerConfig = useMemo(() => {
      const baseConfig = INVESTMENT_BUILDING_CONTEXT_TABLE_CONFIG.pickerConfig;
      return {
        ...baseConfig,
        fieldMappings: baseConfig.fieldMappings.map((mapping) => {
          if (mapping.eventMode === "label") {
            return {
              ...mapping,
              customOptions: renovationLabels.map((label) => ({
                value: String(label.id),
                label: `${label.name} (${label.type})`,
              })),
            };
          }
          if (mapping.eventMode === "bucket") {
            return {
              ...mapping,
              customOptions: buckets.map((b) => ({
                value: String(b.id),
                label: `${b.name}${b.year ? ` (${b.year})` : ""}${
                  b.isApproved ? "" : " · niet goedgekeurd"
                }`,
              })),
            };
          }
          return mapping;
        }),
      };
    }, [renovationLabels, buckets]);

    // Expose section ref for scroll detection
    useEffect(() => {
      if (sectionRef.current) {
        sectionRef.current.setAttribute("data-section", "investment-items");
      }
    }, []);

    return (
      <>
        <div ref={sectionRef} className="flex flex-col gap-3" data-section="investment-items">
          <header className="flex justify-between items-center">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              {isExpanded ? (
                <CaretUp weight="bold" size={16} className="text-neutral-500" />
              ) : (
                <CaretDown weight="bold" size={16} className="text-neutral-500" />
              )}
              <ListBullets weight="duotone" size={18} />
              <p className="text-lg font-semibold">Budget detailverdeling</p>
              <span className="text-sm text-neutral-500">
                ({items.length} items)
              </span>
            </button>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleNavigateToInvestmentList}
                variant="outline"
                size="sm"
              >
                <ArrowSquareOut className="h-4 w-4 mr-2" />
                Alle budget detailverdelingen
              </Button>
              <Button
                onClick={handleAddInvestmentItem}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nieuwe budgetlijn
              </Button>
            </div>
          </header>

          {/* Budget pipeline filter pills — portalled above the strategische
              budgetten section so the pipeline overview shows at the very top
              of the renovation tab. State stays here because it controls the
              table filter below. */}
          {isExpanded && (
            <PortalToBudgetPipeline>
              <BudgetPipelineFilter
                active={budgetFilter}
                onChange={handleBudgetPillClick}
                buckets={budgetBuckets}
                // Offertes hangen aan het gebouw, niet aan een investeringslijn,
                // dus dit cijfer komt van de backend en niet uit `localItems`.
                approvedQuotesTotal={buildingTotals.totalApprovedQuotes}
              />
            </PortalToBudgetPipeline>
          )}

          {/* Active-filter bar — surfaces that the table is currently scoped
              to one of the budget pipeline buckets and lets the user clear it
              with a single click. "Volledig budget" is the unfiltered state. */}
          {isExpanded && budgetFilter !== "totaal" && (
            <div className="flex items-center justify-between rounded-md border border-moss-primary/20 bg-moss-primary/5 px-3 py-2 text-sm text-moss-primary">
              <div className="flex items-center gap-2">
                <FunnelSimple className="h-4 w-4 shrink-0" weight="duotone" />
                <span>
                  Gefilterd op{" "}
                  <span className="font-semibold">
                    {BUDGET_FILTER_LABELS[budgetFilter]}
                  </span>{" "}
                  · {items.length} resultat
                  {items.length === 1 ? "" : "en"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setBudgetFilter("totaal")}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-moss-primary/10 transition-colors"
                title="Wis filter"
              >
                <X className="h-3 w-3" weight="bold" />
                Wis filter
              </button>
            </div>
          )}

          {/* Data-quality KPI: project parts not linked to a budget */}
          {isExpanded && buildingTotals.unlinkedPartsCount > 0 && (
            <div
              className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              title="Projectonderdelen die nog niet gekoppeld zijn aan een investeringslijn (geen budget toegewezen)"
            >
              <LinkBreak className="h-4 w-4 shrink-0" weight="duotone" />
              <span>
                <span className="font-semibold">
                  {buildingTotals.unlinkedPartsCount}
                </span>{" "}
                projectonderde
                {buildingTotals.unlinkedPartsCount === 1 ? "el" : "len"} niet
                gelinkt aan een budget
              </span>
            </div>
          )}

          {/* Selection Action Bar */}
          {isExpanded && selectedRows.size > 0 && (
            <div className="rounded-lg border border-moss-primary/30 bg-moss-primary/10 p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-900">
                    {selectedItemsStats.count} item(s) geselecteerd
                  </span>
                  <div className="h-4 w-px bg-gray-300"></div>
                  <span className="text-sm font-semibold text-moss-primary">
                    Totaal:{" "}
                    {new Intl.NumberFormat("nl-BE", {
                      style: "currency",
                      currency: "EUR",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(selectedItemsStats.totalCost)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedRows(new Set())}
                  >
                    Selectie wissen
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleConvertToProject}
                    className="bg-moss-primary hover:bg-moss-primaryDark"
                  >
                    <Rocket className="h-4 w-4 mr-2" />
                    Creëer renovatieproject
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Table - embedded mode: sticky header sticks at top:0 instead of below navbar */}
          {isExpanded && (
            <div className="rounded-xl border border-neutral-200 shadow-sm bg-white embedded-table-wrapper">
              <UniversalTable
                columnsConfig={INVESTMENT_BUILDING_CONTEXT_TABLE_CONFIG.columnsConfig}
                pickerConfig={dynamicPickerConfig}
                sortConfig={INVESTMENT_BUILDING_CONTEXT_TABLE_CONFIG.sortConfig}
                rows={items}
                groupBy="none"
                title=""
                renderRow={renderRow}
                footerCells={renderFooterCells}
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                onUpdate={handleUpdate}
                isLoading={isLoading}
              />
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <Warning size={24} className="text-red-600" weight="fill" />
                </div>
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Item verwijderen
                </DialogTitle>
              </div>
            </DialogHeader>
            <DialogDescription className="text-gray-600">
              Weet u zeker dat u het item{" "}
              <strong className="text-gray-900">{deletingItem?.title}</strong> wilt
              verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </DialogDescription>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeletingItem(null);
                }}
              >
                Annuleren
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash className="h-4 w-4 mr-2" />
                Verwijderen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Investment Item Modal */}
        <InvestmentItemModal
          isOpen={createModalOpen || editModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            setEditModalOpen(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            handleModalSuccess();
            // Create/edit can change estimatedCostExclVat or bucket — both
            // affect bucket "used" via BE transactions. Refresh cards.
            dispatchBudgetBucketsChanged(buildingId);
          }}
          editingItem={editingItem as any}
          defaultBuildingId={createModalOpen ? buildingId : undefined}
        />

        {/* Convert to Project Modal */}
        <ConvertToProjectModal
          isOpen={convertModalOpen}
          onClose={() => setConvertModalOpen(false)}
          onSuccess={() => {
            fetchItems();
            setSelectedRows(new Set());
          }}
          selectedItems={selectedItemsStats.items as any}
          buildingId={buildingId}
        />

        {/* Budget Change Modal */}
        <ChangeWithReasonModal
          isOpen={budgetChangeModalOpen}
          onClose={() => {
            setBudgetChangeModalOpen(false);
            setBudgetChangeItem(null);
          }}
          onSuccess={() => {
            fetchItems();
            // Budget change writes an ADJUSTMENT on the line's bucket.
            dispatchBudgetBucketsChanged(buildingId);
          }}
          item={budgetChangeItem as any}
          changeType="budget"
          onTransferRequest={handleTransferRequest as any}
        />

        {/* Status Change Modal */}
        <ChangeWithReasonModal
          isOpen={statusChangeModalOpen}
          onClose={() => {
            setStatusChangeModalOpen(false);
            setStatusChangeItem(null);
          }}
          onSuccess={() => {
            fetchItems();
            // Closing / rejecting a line releases its remainder back to its
            // bucket, so re-fetch the cards to show the updated balance.
            dispatchBudgetBucketsChanged(buildingId);
          }}
          item={statusChangeItem as any}
          changeType="status"
        />

        {/* Budget Transfer Modal */}
        {transferSourceItem && (
          <BudgetTransferModal
            isOpen={transferModalOpen}
            onClose={() => {
              setTransferModalOpen(false);
              setTransferSourceItem(null);
              setTransferFreedAmount(0);
            }}
            onSuccess={() => {
              fetchItems();
            }}
            sourceItem={transferSourceItem as any}
            freedAmount={transferFreedAmount}
          />
        )}

        {/* Complete (close) Investment Line Modal */}
        <CompleteInvestmentLineModal
          isOpen={completeModalOpen}
          onClose={() => {
            setCompleteModalOpen(false);
            setCompleteItem(null);
          }}
          item={completeItem as any}
          onSuccess={() => {
            // Refresh items so the line shows the new "Afgesloten" status,
            // and refresh the bucket cards so the released remainder lands
            // visibly back in the source pot.
            fetchItems();
            dispatchBudgetBucketsChanged(buildingId);
          }}
        />

        {/* Release dialog — confirm + capture optional audit reason */}
        <ReleaseInvestmentItemDialog
          isOpen={releasingItem !== null}
          item={releasingItem as any}
          onClose={() => setReleasingItem(null)}
          onConfirm={handleReleaseConfirm}
        />

        {/* Multi-pot bucket allocation dialog */}
        <BucketAllocationDialog
          item={bucketAllocItem as any}
          isOpen={bucketAllocItem !== null}
          onClose={() => setBucketAllocItem(null)}
          onSaved={() => {
            setBucketAllocItem(null);
            fetchItems();
          }}
        />

        {/* Budget split designer — entry point for "Nieuwe budgetlijn".
            Bucket is null on open so the designer's in-header pot picker
            is shown (incl. Overflow). */}
        <BudgetSplitDesigner
          bucket={null}
          buildingId={buildingId}
          isOpen={splitDesignerOpen}
          onClose={() => setSplitDesignerOpen(false)}
          onSaved={() => {
            setSplitDesignerOpen(false);
            fetchItems();
            dispatchBudgetBucketsChanged(buildingId);
          }}
        />
      </>
    );
  }
);

InvestmentItemsSection.displayName = "InvestmentItemsSection";
